import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import { listCatalogueModelImages, saveCatalogueModelImage } from "../common/warrantyApi";
import { requestIdFor, settleRequestId } from "../common/clientRequest";
import ImagePreview from "./ImagePreview";
import {
  EMPTY_MODEL_FORM,
  PRODUCT_GROUP_MAX,
  URL_MAX,
  modelFormErrors,
  modelFormFrom,
  modelImagePayload,
  updatedText,
} from "./catalogueFields";
import "../warranty.css";

const FieldMsg = ({ id, text }) =>
  text ? (
    <span id={id} className="warranty-field-msg is-error" role="alert">
      {text}
    </span>
  ) : null;

// A cycle's photo is copied from the website when its warranty is registered; the rows saved here
// are overrides for a model the website has no photo for, or to replace that photo.
const ModelImages = ({ country, accessToken }) => {
  const [view, setView] = useState({ status: "loading", data: null, message: "" });
  const [form, setForm] = useState(EMPTY_MODEL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const seq = useRef(0);
  const mounted = useRef(true);
  const formRef = useRef(null);
  // A retry of the same override after an unclear answer reuses its request id.
  const requestId = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (!quiet) {
        setView({ status: "loading", data: null, message: "" });
      }
      const result = await listCatalogueModelImages(accessToken, country);
      if (!mounted.current || mine !== seq.current) {
        return;
      }
      if (result.ok) {
        setView({ status: "ready", data: result.data || {}, message: "" });
      } else if (!quiet) {
        setView({ status: "error", data: null, message: result.message });
      } else {
        toast.error(result.message);
      }
    },
    [accessToken, country]
  );

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => (Array.isArray(view.data?.items) ? view.data.items : []), [view.data]);
  const groups = useMemo(
    () => (Array.isArray(view.data?.productGroups) ? view.data.productGroups.filter((g) => typeof g === "string") : []),
    [view.data]
  );

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaveError("");
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const edit = (item) => {
    setForm(modelFormFrom(item));
    setErrors({});
    setSaveError("");
    // Focus brings the form into view as well.
    const first = formRef.current ? formRef.current.querySelector("input") : null;
    if (first) {
      first.focus();
    }
  };

  const save = async () => {
    const found = modelFormErrors(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    setSaving(true);
    setSaveError("");
    const body = modelImagePayload(country, form);
    const result = await saveCatalogueModelImage(accessToken, body, requestIdFor(requestId, JSON.stringify(body)));
    settleRequestId(requestId, result);
    if (!mounted.current) {
      return;
    }
    setSaving(false);
    if (result.ok) {
      toast.success("Override saved.");
      setForm(EMPTY_MODEL_FORM);
      load(true);
    } else {
      setSaveError(result.message);
    }
  };

  if (view.status === "loading") {
    return <Loading label="Loading the model images" />;
  }
  if (view.status === "error") {
    return <ErrorState message={view.message} onRetry={() => load()} />;
  }

  return (
    <div className="warranty-catalogue-models">
      <div className="warranty-catalogue-head">
        <h5>Overrides</h5>
        <button type="button" className="warranty-inline-btn" onClick={() => load(true)}>
          Refresh
        </button>
      </div>
      <p className="warranty-dialog-hint">
        Each cycle's photo is copied from the website automatically when its warranty is registered. Add
        an override here only for a model the website has no photo for, or to replace it.
      </p>
      {items.length === 0 ? (
        <EmptyState title="No overrides for this market" />
      ) : (
        <div className="tableView warranty-ops-table">
          <table className="table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Product group</th>
                <th>Product</th>
                <th>Image URL</th>
                <th>Active</th>
                <th>Updated</th>
                <th className="warranty-actions-cell">
                  <span className="warranty-sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id || `${item.productGroup}-${item.productId}`}>
                  <td>
                    <ImagePreview src={item.imageUrl} size={80} alt={`${item.productGroup || ""} image`} />
                  </td>
                  <td>{item.productGroup || "-"}</td>
                  <td>{item.productId ? <code>{item.productId}</code> : "All products in group"}</td>
                  <td className="warranty-catalogue-url-text">{item.imageUrl || "No image"}</td>
                  <td>
                    <span className={`warranty-badge${item.active ? " is-live" : ""}`}>
                      {item.active ? "Active" : "Off"}
                    </span>
                  </td>
                  <td className="warranty-nowrap">{updatedText(item)}</td>
                  <td className="warranty-actions-cell">
                    <button
                      type="button"
                      className="warranty-inline-btn"
                      onClick={() => edit(item)}
                      aria-label={`Edit ${item.productGroup}${item.productId ? ` ${item.productId}` : ""}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form
        ref={formRef}
        className="formloginset warranty-section warranty-catalogue-form"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
        autoComplete="off"
        noValidate
      >
        <h5>Add or change an override</h5>
        <p className="warranty-help">
          One image per product group, or per product inside a group. Saving the same group and product
          again replaces its image.
        </p>
        <div className="warranty-catalogue-fields">
          <Form.Group controlId="warrantyCatalogueGroup">
            <Form.Label>Product group</Form.Label>
            <Form.Control
              type="text"
              list="warranty-catalogue-groups"
              value={form.productGroup}
              maxLength={PRODUCT_GROUP_MAX}
              placeholder="Choose or type a group"
              isInvalid={Boolean(errors.productGroup)}
              onChange={(event) => setField("productGroup", event.target.value)}
              disabled={saving}
            />
            <datalist id="warranty-catalogue-groups">
              {groups.map((group) => (
                <option key={group} value={group} />
              ))}
            </datalist>
            <FieldMsg id="warranty-catalogue-group-msg" text={errors.productGroup} />
          </Form.Group>
          <Form.Group controlId="warrantyCatalogueProduct">
            <Form.Label>
              Product id <span className="warranty-help-inline">(optional, blank for all products in the group)</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.productId}
              maxLength={36}
              placeholder="0199a1b2-0000-7000-8000-000000000001"
              isInvalid={Boolean(errors.productId)}
              onChange={(event) => setField("productId", event.target.value)}
              disabled={saving}
              spellCheck={false}
            />
            <FieldMsg id="warranty-catalogue-product-msg" text={errors.productId} />
          </Form.Group>
          <Form.Group controlId="warrantyCatalogueImage">
            <Form.Label>Image URL</Form.Label>
            <Form.Control
              type="url"
              value={form.imageUrl}
              maxLength={URL_MAX}
              placeholder="https://cdn.emotorad.com/... (blank for none)"
              isInvalid={Boolean(errors.imageUrl)}
              onChange={(event) => setField("imageUrl", event.target.value)}
              disabled={saving}
              spellCheck={false}
            />
            <FieldMsg id="warranty-catalogue-image-msg" text={errors.imageUrl} />
          </Form.Group>
          <div className="warranty-catalogue-form-preview">
            <ImagePreview src={form.imageUrl.trim()} size={80} alt="Image preview" />
          </div>
        </div>
        <Form.Check
          type="switch"
          id="warranty-catalogue-active"
          label="Active"
          checked={form.active}
          onChange={(event) => setField("active", event.target.checked)}
          disabled={saving}
        />
        {saveError && (
          <div className="warranty-dialog-error" role="alert">
            {saveError}
          </div>
        )}
        <div className="warranty-card-actions">
          <Button className="Addbtn" type="submit" disabled={saving}>
            {saving ? "Saving" : "Save"}
          </Button>
          <Button
            variant="secondary"
            type="button"
            disabled={saving}
            onClick={() => {
              setForm(EMPTY_MODEL_FORM);
              setErrors({});
              setSaveError("");
            }}
          >
            Clear
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ModelImages;
